var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box } from '@mui/material';
import React from 'react';
import { themeColors, themeMode } from '../JuvareTheme';
/**
 * A card component with visual feedback for click and keyboard interaction.
 *
 * @template {ClickableCardProps} ClickableCardProps - Props for the ClickableCard component.
 * @param {ClickableCardProps} props - The props for the ClickableCard.
 * @param {() => void} [props.onCardClick] - Optional click handler for the card.
 * @param {string} [props.cardAriaLabel] - Accessible label for the card when used as a button.
 * @param {SxProps<Theme> | SxProps<Theme>[]} [props.sx] - Style overrides applied to the card.
 * @param {React.ReactNode} props.children - Content to render inside the card.
 * @returns {JSX.Element} A clickable card component with conditional hover and keyboard behavior.
 */
export const ClickableCard = (_a) => {
    var { onCardClick, cardAriaLabel, sx, children } = _a, props = __rest(_a, ["onCardClick", "cardAriaLabel", "sx", "children"]);
    return (React.createElement(Box, { sx: [
            themeMode === 'light'
                ? {
                    position: 'relative',
                    backgroundColor: 'background.paper',
                    border: '1px solid ' + themeColors.border.main,
                    borderRadius: 1,
                    outline: 'none',
                    cursor: onCardClick ? 'pointer' : undefined,
                    '&:hover': onCardClick
                        ? {
                            boxShadow: 4,
                        }
                        : undefined,
                    '&:focus-visible': onCardClick
                        ? {
                            outline: '1px solid ' + themeColors.accent.blue.base,
                        }
                        : undefined,
                    transition: 'box-shadow 150ms ease-in-out',
                }
                : {
                    position: 'relative',
                    backgroundColor: 'background.paper',
                    border: '1px solid ' + themeColors.border.main,
                    borderRadius: 1,
                    outline: '1px solid transparent',
                    cursor: onCardClick ? 'pointer' : undefined,
                    '&:hover': onCardClick
                        ? {
                            backgroundColor: themeColors.accent.blue.subtlest,
                            outline: '1px solid ' + themeColors.accent.blue.base,
                        }
                        : undefined,
                    '&:focus-visible': onCardClick
                        ? {
                            outline: '1px solid ' + themeColors.accent.blue.base,
                        }
                        : undefined,
                    transition: 'background-color 150ms ease-in-out, outline-color 150ms ease-in-out',
                },
            ...(Array.isArray(sx) ? sx : [sx]),
        ], tabIndex: onCardClick ? 0 : undefined, onKeyDown: onCardClick
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    onCardClick();
                }
            }
            : undefined, onClick: onCardClick ? () => onCardClick() : undefined, role: 'button', "aria-label": cardAriaLabel, "data-test": props['data-test'] }, children));
};
